import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress,
    CircularProgress,
    Alert,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Tabs,
    Tab,
    Tooltip
} from '@mui/material';
import api from '../api';
import config from '../config';
import * as XLSX from 'xlsx';
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

// Сокращенные названия разделов для компактного отображения
const sectionShortNames = {
    common: 'main',
    struct: 'struct',
    document: 'doc',
    education: 'edu',
    edustandarts: 'stand',
    managers: 'mgmt',
    employees: 'staff',
    objects: 'obj',
    grants: 'grants',
    paid_edu: 'paid',
    budget: 'budget',
    vacant: 'vac',
    inter: 'int',
    catering: 'food'
};

const HomePage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [ous, setOus] = useState([]);
    const [filteredOus, setFilteredOus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ouTypes, setOuTypes] = useState([]);
    const [selectedType, setSelectedType] = useState('all');
    const [activeTab, setActiveTab] = useState(0);
    const [sectionResults, setSectionResults] = useState([]);

    const getNumericPart = (str) => {
        const match = str?.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
    };

    const sortOUs = useCallback((data) => {
        return [...data].sort((a, b) => {
            const numA = getNumericPart(a.number);
            const numB = getNumericPart(b.number);
            return numA - numB;
        });
    }, []);

    const fetchSectionResults = useCallback(async () => {
        try {
            const response = await api.get(`${config.apiBaseUrl}/admin/section-results`);
            if (response.data && Array.isArray(response.data)) {
                setSectionResults(response.data);
                return true;
            }
            throw new Error('Неверный формат данных разделов');
        } catch (err) {
            console.error('Ошибка при загрузке данных разделов:', err);
            setError('Ошибка при загрузке данных разделов');
            return false;
        }
    }, []);

    const fetchOUsWithCompletion = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [ouResponse, completionResponse] = await Promise.all([
                api.get(`${config.apiBaseUrl}/admin/ou-links`),
                api.get(`${config.apiBaseUrl}/admin/completionpercentages`)
            ]);

            if (!ouResponse.data || !Array.isArray(ouResponse.data)) {
                throw new Error('Неверный формат данных учреждений');
            }

            if (!completionResponse.data || !Array.isArray(completionResponse.data)) {
                throw new Error('Неверный формат данных заполненности');
            }

            const ousWithCompletion = ouResponse.data.map(ou => {
                const completion = completionResponse.data.find(c => c.ou_id === ou.id);
                return {
                    ...ou,
                    completionPercentage: completion ? completion.completion_percentage : 0
                };
            });

            const sortedOus = sortOUs(ousWithCompletion);
            setOus(sortedOus);
            setOuTypes([...new Set(ouResponse.data.map(ou => ou.type))]);

            const sectionsLoaded = await fetchSectionResults();
            if (!sectionsLoaded) {
                throw new Error('Не удалось загрузить данные разделов');
            }

        } catch (err) {
            console.error('Ошибка при загрузке данных:', err);
            setError(err.message || 'Ошибка при загрузке данных');
            return false;
        } finally {
            setLoading(false);
        }
    }, [sortOUs, fetchSectionResults]);

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const loadData = async () => {
            try {
                await fetchOUsWithCompletion();
            } catch (err) {
                if (isMounted) {
                    console.error('Ошибка в loadData:', err);
                    setError(err.message || 'Ошибка при загрузке данных');
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [fetchOUsWithCompletion]);

    useEffect(() => {
        if (ous.length > 0) {
            const filtered = selectedType === 'all' ? ous : ous.filter(ou => ou.type === selectedType);
            setFilteredOus(sortOUs(filtered));
        }
    }, [ous, selectedType, sortOUs]);

    const refreshData = async () => {
        try {
            setError(null);
            setLoading(true);
            const success = await fetchOUsWithCompletion();
            if (!success) {
                throw new Error('Не удалось обновить данные');
            }
        } catch (err) {
            setError(err.message || 'Ошибка при обновлении данных');
        }
    };

    const exportToExcel = () => {
        try {
            if (filteredOus.length === 0) {
                throw new Error('Нет данных для экспорта');
            }

            const worksheet = XLSX.utils.json_to_sheet(
                filteredOus.map(ou => ({
                    '№ ОУ': ou.number,
                    'Тип учреждения': ou.type,
                    'Ссылка на сайт': ou.link,
                    'Процент заполненности': ou.completionPercentage
                }))
            );
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Заполненность сайтов");
            XLSX.writeFile(workbook, "Заполненность_сайтов_ОУ.xlsx");
        } catch (err) {
            console.error('Ошибка при экспорте в Excel:', err);
            setError(err.message || 'Ошибка при экспорте данных');
        }
    };

    const exportSectionResultsToExcel = () => {
        try {
            if (filteredOus.length === 0 || sectionResults.length === 0) {
                throw new Error('Нет данных для экспорта');
            }

            const data = filteredOus.map(ou => {
                const ouResults = sectionResults.filter(r => r.ou_id === ou.id);
                const row = {
                    '№ ОУ': ou.number,
                    'Тип учреждения': ou.type,
                    'Ссылка на сайт': ou.link
                };

                ['common', 'struct', 'document', 'education', 'edustandarts', 'managers',
                    'employees', 'objects', 'grants', 'paid_edu', 'budget', 'vacant', 'inter', 'catering']
                    .forEach(section => {
                        const sectionData = ouResults.find(r => r.section === section);
                        row[`${section}_errors`] = sectionData ? `${sectionData.error_count}/${sectionData.total_tags}` : 'нет данных';
                        row[`${section}_percentage`] = sectionData ? `${100 - sectionData.error_percentage}%` : 'нет данных';
                    });

                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Ошибки по разделам");
            XLSX.writeFile(workbook, "Ошибки_по_разделам_ОУ.xlsx");
        } catch (err) {
            console.error('Ошибка при экспорте в Excel:', err);
            setError(err.message || 'Ошибка при экспорте данных');
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const renderSectionStatus = (ouId, section) => {
        const sectionData = sectionResults.find(r => r.ou_id === ouId && r.section === section);
        const successPercentage = sectionData ? 100 - sectionData.error_percentage : 0;

        if (!sectionData) {
            return (
                <Tooltip title="Данные раздела отсутствуют">
                    <HelpOutlineIcon color="disabled" fontSize="small" />
                </Tooltip>
            );
        }

        return (
            <Tooltip title={`Ошибок: ${sectionData.error_count}/${sectionData.total_tags} (Успешно: ${successPercentage}%)`}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {successPercentage === 100 ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                        <Typography
                            variant="caption"
                            fontWeight="bold"
                            color={
                                successPercentage >= 70 ? 'success.main' :
                                    successPercentage >= 40 ? 'warning.main' : 'error.main'
                            }
                        >
                            {sectionData.error_count}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
        );
    };

    if (loading && !error) {
        return (
            <Container maxWidth="lg">
                <Box mt={5} display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Box mt={5}>
                    <Alert severity="error" action={
                        <Button color="inherit" size="small" onClick={refreshData}>
                            Обновить
                        </Button>
                    }>
                        {error}
                    </Alert>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
            <Box mt={isMobile ? 3 : 5}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ fontWeight: 600 }}>
                    Обзор заполненности сайтов ОУ
                </Typography>

                <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                    <Typography variant="body2" fontStyle="italic" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                        Приказ Рособрнадзора от 04.08.2023 N 1493 п 27: Все страницы официального Сайта должны содержать html-разметку,
                        определяющую наличие соответствующей информации.
                    </Typography>
                </Box>

                <Box mb={3}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={isMobile ? "fullWidth" : "standard"}
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label="Общий обзор" />
                        <Tab label="Ошибки по разделам" />
                    </Tabs>
                </Box>

                <Box mb={3} display="flex" flexDirection={isMobile ? "column" : "row"} alignItems={isMobile ? "stretch" : "center"} gap={2}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel id="ou-type-filter-label">Тип учреждения</InputLabel>
                        <Select
                            labelId="ou-type-filter-label"
                            value={selectedType}
                            label="Тип учреждения"
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            <MenuItem value="all">Все типы</MenuItem>
                            {ouTypes.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box display="flex" gap={2} sx={{ width: isMobile ? '100%' : 'auto' }}>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={activeTab === 0 ? exportToExcel : exportSectionResultsToExcel}
                            disabled={loading || filteredOus.length === 0}
                            fullWidth={isMobile}
                            size={isMobile ? "medium" : "small"}
                        >
                            Экспорт в Excel
                        </Button>

                        <Button
                            variant="contained"
                            color="info"
                            startIcon={<DescriptionIcon />}
                            href="https://obrnadzor.gov.ru/wp-content/uploads/2024/09/metodicheskie-rekomendaczii-predstavleniya-informaczii-ob-obrazovatelnoj-or....pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            fullWidth={isMobile}
                            size={isMobile ? "medium" : "small"}
                            sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#1565c0',
                                }
                            }}
                        >
                            {isMobile ? 'Рекомендации' : 'Методические рекомендации'}
                        </Button>
                    </Box>
                </Box>

                {activeTab === 0 ? (
                    isMobile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {filteredOus.map((ou) => (
                                <Card key={ou.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                № {ou.number}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {ou.type}
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                                            <Box component="span" fontWeight="bold">Сайт: </Box>
                                            <a
                                                href={ou.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: theme.palette.primary.main,
                                                    wordBreak: 'break-all',
                                                    display: 'inline-block',
                                                    marginTop: '4px'
                                                }}
                                            >
                                                {ou.link}
                                            </a>
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" sx={{ mr: 1 }}>
                                                <Box component="span" fontWeight="bold">Заполненность: </Box>
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: ou.completionPercentage > 70
                                                        ? '#4caf50'
                                                        : ou.completionPercentage > 40
                                                            ? '#ff9800'
                                                            : '#f44336'
                                                }}
                                            >
                                                {ou.completionPercentage}%
                                            </Typography>
                                        </Box>

                                        <LinearProgress
                                            variant="determinate"
                                            value={ou.completionPercentage}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 4,
                                                    backgroundColor: ou.completionPercentage > 70
                                                        ? '#4caf50'
                                                        : ou.completionPercentage > 40
                                                            ? '#ff9800'
                                                            : '#f44336'
                                                }
                                            }}
                                        />
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ py: 1, width: '15%' }}>№ ОУ</TableCell>
                                        <TableCell sx={{ py: 1, width: '20%' }}>Тип учреждения</TableCell>
                                        <TableCell sx={{ py: 1, width: '45%' }}>Ссылка на сайт</TableCell>
                                        <TableCell sx={{ py: 1, width: '20%' }}>Заполненность</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOus.map((ou) => (
                                        <TableRow key={ou.id} hover>
                                            <TableCell>{ou.number}</TableCell>
                                            <TableCell>{ou.type}</TableCell>
                                            <TableCell>
                                                <a
                                                    href={ou.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: theme.palette.primary.main,
                                                        wordBreak: 'break-all',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {ou.link}
                                                </a>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <Box width="100%" mr={1}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={ou.completionPercentage}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 4,
                                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                                '& .MuiLinearProgress-bar': {
                                                                    borderRadius: 4,
                                                                    backgroundColor: ou.completionPercentage > 70
                                                                        ? '#4caf50'
                                                                        : ou.completionPercentage > 40
                                                                            ? '#ff9800'
                                                                            : '#f44336'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            minWidth: 35,
                                                            fontWeight: 'bold',
                                                            color: ou.completionPercentage > 70
                                                                ? '#4caf50'
                                                                : ou.completionPercentage > 40
                                                                    ? '#ff9800'
                                                                    : '#f44336'
                                                        }}
                                                    >
                                                        {ou.completionPercentage}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 3,
                                            backgroundColor: 'background.paper',
                                            minWidth: 100,
                                            fontWeight: 'bold'
                                        }}>
                                            № ОУ
                                        </TableCell>
                                        {Object.keys(sectionShortNames).map(section => (
                                            <TableCell
                                                key={section}
                                                align="center"
                                                sx={{
                                                    minWidth: 60,
                                                    px: 1,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                <Tooltip title={config.sectionNames[section] || section}>
                                                    <span>{sectionShortNames[section]}</span>
                                                </Tooltip>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOus.map(ou => (
                                        <TableRow key={ou.id} hover>
                                            <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor: 'background.paper',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {ou.number}
                                            </TableCell>
                                            {Object.keys(sectionShortNames).map(section => {
                                                const sectionData = sectionResults.find(r => r.ou_id === ou.id && r.section === section);
                                                const successPercentage = sectionData ? 100 - sectionData.error_percentage : 0;

                                                return (
                                                    <TableCell
                                                        key={`${ou.id}-${section}`}
                                                        align="center"
                                                        sx={{
                                                            px: 1,
                                                            backgroundColor: successPercentage === 100
                                                                ? theme.palette.success.light
                                                                : 'inherit'
                                                        }}
                                                    >
                                                        {renderSectionStatus(ou.id, section)}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default HomePage;